import {
    DocumentType,
    LinkedProjectConfig,
    Note,
    ProjectCheckConfig,
    ProjectCheckFinding,
    ProjectCheckResult,
    ProjectCheckRule,
    ProjectCheckTraffic,
    ProjectStatus,
    ServiceItem,
    ServiceTransaction,
} from '../types';
import { calculateStats } from '../components/project-hub/calculateStats';
import { contractedQuantity } from './nepPosition';
import { asDay, isAccountingEntry, loggedQuantity, postedQuantity } from './serviceLogUtils';
import { bestandteilSperrText, isHourlyUnit, openQuantity, ownReportProofCount, waivedQuantity } from './projectLv';
import {
    bestandteilGate,
    hatBestandteile,
    istAbrechnungGesperrt,
    normalisiereBestandteile,
    zuordnungFuerQuelle,
} from './bestandteile/bestandteilLogic';
import { isFlatRateService } from './flatRateLogic';
import { bookedReportNumbers } from './reportBooking';
import { Cents, formatCents, roundCents } from './money';
import { DEFAULT_PROJECT_CHECK_CONFIG as DEFAULTS } from './invoiceConfig';
import { BOOKING_EPSILON } from './invoiceWriteBackLogic';

/**
 * Projektcheck vor der Rechnung.
 *
 * Pure Funktionen, "heute" wird hereingereicht — kein Date.now(), kein
 * Datenbankzugriff, keine React-Abhaengigkeit. Genau deshalb sind die Regeln
 * testbar, und genau das unterscheidet sie von den heutigen Regeln in
 * AdminAlertsView, die als Closure in einer Komponente stecken.
 *
 * Der Zweck ist nicht, Rechnungen zu verhindern, sondern Datendrift sichtbar zu
 * machen, BEVOR sie in einen festgeschriebenen Beleg wandert. Ein falscher
 * Leistungsstand laesst sich vor der Ausstellung in einer Minute korrigieren;
 * danach braucht es Storno und Neuausstellung.
 *
 * Ampel:
 *   gruen  — nichts gefunden, freigeben und ausstellen
 *   gelb   — auffaellig, Ausstellen nur mit dokumentierter Kenntnisnahme
 *   rot    — Ausstellen gesperrt, das Projekt muss erst bereinigt werden
 */

// ═══════════════════════════════════════════════════════════════════════════
// Eingaben
// ═══════════════════════════════════════════════════════════════════════════

/** Leistungszeitraum einer bereits ausgestellten Rechnung. */
export interface IssuedPeriod {
    invoiceNumber?: string;
    from?: string;
    to?: string;
}

export interface ProjectCheckInput {
    projectId: string;
    projectName: string;
    projectNumber?: string;
    projectStatus?: ProjectStatus;
    /** Fehlt er, gibt es keinen Rechnungsempfaenger. */
    customerId?: string;
    startDate?: string;

    services: ServiceItem[];
    serviceLog: ServiceTransaction[];
    notes?: Note[];

    /**
     * Metadaten der archivierten Berichte — die leichte Liste, nicht die Berichte selbst.
     *
     * `billingExempt`: der Bericht ist ausdruecklich NICHT abzurechnen
     * (`report_history.billing_exempt`, „Nicht abrechenbar"). Er belegt eine
     * Nummer und eine Archivzeile, aber keine Leistung — Regel 1 und 2 erwarten
     * fuer ihn keine Buchung. Fall 6253: #20 war ein Fehlversand des alten
     * Entwurfs, die Begehung lief als #21; ohne die Ausnahme meldete der Zaehler
     * auf Dauer eine ungebuchte Begehung.
     */
    reportHistory?: Array<{ inspectionNumber?: number; date?: string; billingExempt?: boolean }>;

    /**
     * Fuer dieses Projekt entstehen keine Berichte in der Anwendung.
     *
     * Es gibt Auftraege, bei denen nur Leistungen erfasst und abgerechnet
     * werden — Beratung, Begehungen mit Bericht des Auftraggebers, Uebernahmen
     * aus einem Fremdsystem. Dort ist das leere Berichtsarchiv kein Datenfehler,
     * sondern die Arbeitsweise, und Regel 1 sperrte jede Rechnung.
     *
     * Bewusst ein Kennzeichen AM PROJEKT und keine globale Einstellung: wer die
     * Pruefung fuer alle Projekte abschaltet, verliert sie dort, wo sie traegt.
     */
    reportExempt?: boolean;
    /**
     * SiFa-Projekt: unbefristete Betreuung eines Betriebs, keine Baustelle
     * (docs/sifa-betreuung-plan.md).
     *
     * Drei Regeln setzen eine Baustelle voraus und schweigen deshalb hier:
     * Regel 9 (Vorankuendigung/Unterlage — gibt es bei einer Betreuung nicht),
     * Regel 10 (Schlussrechnung nur bei `finished` — eine Dauerbetreuung wird
     * nie fertig, die Schlussrechnung schliesst dort das Vertragsjahr ab) und
     * Regel 14 (liegengebliebene Position — Unterweisungen und Fristen laufen
     * ohne festen Rhythmus). Die Berichtsregeln 1 und 2 bleiben scharf: wer
     * bei SiFa Begehungen bucht, schreibt auch Berichte.
     */
    sifa?: boolean;
    /**
     * Nummer der aktuellen Begehung.
     *
     * Wie viele davon ERLEDIGT sind, haengt daran, ob der Bericht mit dieser
     * Nummer schon archiviert ist — das entscheidet Regel 2 anhand der
     * hoechsten Nummer in reportHistory, nicht pauschal ueber "minus eins".
     */
    currentInspectionNumber?: number;
    /**
     * Begehungstag und Abschnitt des laufenden Berichts (`metadata.date`,
     * `metadata.sectionId`). Nur für den Integritätswächter
     * (`versendet_ohne_archiv`): er ordnet eine Versandmail damit genau diesem
     * Bericht zu und nicht einem älteren gleicher Nummer.
     */
    currentReportDate?: string;
    currentReportSectionId?: string | null;

    vorankuendigung?: {
        history?: Array<{ date: string; action: 'created' | 'revised' }>;
        archive?: Array<{ id: string; date: string }>;
    };

    linkedProjectConfig?: LinkedProjectConfig;

    /** service_id -> Menge, die ein anderer offener Entwurf bereits beansprucht. */
    reservations?: Map<string, number>;

    /** Leistungszeitraeume bereits ausgestellter Rechnungen dieses Projekts. */
    issuedPeriods?: IssuedPeriod[];

    /**
     * Der zu pruefende Beleg rechnet kumulativ ab.
     *
     * Dann entfaellt die Ueberschneidungspruefung: der Zeitraum reicht immer
     * vom Projektbeginn bis zum Stichtag und ueberschneidet zwangslaeufig jeden
     * frueheren Beleg.
     */
    cumulative?: boolean;

    /** Bereits abgerechnete Abschlaege in Cent. */
    advancesCents?: Cents;

    documentType: DocumentType;
    /** Geplanter Leistungszeitraum der neuen Rechnung. */
    periodFrom?: string;
    periodTo?: string;
    /** Zahlbetrag des Entwurfs, soweit schon bekannt. */
    payableCents?: Cents;

    /**
     * Summe der Sicherheitseinbehalte des Projekts, die noch bei keiner
     * Schlussrechnung freigegeben wurden (docs/sicherheitseinbehalt-plan.md
     * §10, §16).
     *
     * Die Zahl kommt vom Dienst, nicht aus den Leistungen: Nettoeinbehalt
     * (retention − release) ueber die ausgestellten Vorbelege plus die von
     * Hand erfassten Alt-Einbehalte — advanceInvoiceLogic.openRetentionCents.
     * Diese Datei kennt keine Belege und soll keine kennen, genau wie bei
     * advancesCents.
     *
     * Fehlt sie oder ist sie 0, schweigt die Pruefung: ein Projekt ohne
     * Einbehalt soll keine Zeile ueber Einbehalte bekommen.
     */
    openRetentionCents?: Cents;
    /**
     * Was der zu pruefende ENTWURF an frueheren Einbehalten freigibt
     * (totals.retentionReleaseCents). Im kumulativen Modell ist das auf einer
     * Schlussrechnung mit Satz 0 die Summe der abgezogenen Vorbelege; laesst
     * der Entwurf den Satz stehen (releaseRetention false) oder zieht er die
     * Vorbelege nicht ab, ist es 0.
     *
     * Getrennt von openRetentionCents, weil beide verschiedene Fragen
     * beantworten: die eine, was noch einbehalten ist, die andere, ob dieser
     * Beleg es holt. Nur aus beiden zusammen ergibt sich die Warnung.
     */
    retentionReleaseCents?: Cents;
}

// Die Voreinstellung steht in invoiceConfig, weil sie Teil der
// Rechnungskonfiguration ist und dort aus den gespeicherten Einstellungen
// aufgeloest wird. Hier nur importiert — zwei Voreinstellungen waeren zwei
// Wahrheiten.
export { DEFAULT_PROJECT_CHECK_CONFIG } from './invoiceConfig';

// ═══════════════════════════════════════════════════════════════════════════
// Hilfen
// ═══════════════════════════════════════════════════════════════════════════

const SEVERITY: Record<ProjectCheckTraffic, ProjectCheckFinding['severity']> = {
    red: 'high',
    yellow: 'medium',
    green: 'low',
};

/** Belegarten, die auch ohne offene Menge entstehen duerfen. */
const CORRECTION_TYPES = new Set<DocumentType>([
    'CANCELLATION_INVOICE',
    'CORRECTION_INVOICE',
    'COMMERCIAL_CREDIT_INVOICE',
    'SELF_BILLING_CREDIT_NOTE',
]);

/**
 * Eine Position, deren quantityUsed BEGEHUNGEN zaehlt.
 *
 * Stundenpositionen sind ausdruecklich keine: "Sicherheitsbegehung nach
 * Zeitaufwand" (2.7.40, Einheit Stunden) traegt dort einen Stundenstand. Ihn
 * gegen die Zahl archivierter Berichte zu halten vergleicht Stunden mit Stueck
 * — 22 Begehungen zu 28 Stunden meldeten eine Abweichung von 6, obwohl beide
 * Zahlen stimmen.
 *
 * Eine Monatspauschale ebenso wenig, auch mit „Begehung" im Titel: ihr
 * quantityUsed zaehlt gebuchte Monate. Ihre Berichte stehen als
 * Nachweiszeilen im Protokoll und kommen ueber ownReportProofCount hinzu.
 */
const isInspectionService = (s: ServiceItem): boolean =>
    !isFlatRateService(s) &&
    !isHourlyUnit(s.unit) &&
    (s.assignment === 'inspection' ||
        s.lvPosition === '2.7.10' ||
        (s.title || '').toLowerCase().includes('begehung'));

/**
 * Der Begehungsstand des Leistungsverzeichnisses, Stundenpositionen ausgenommen.
 *
 * EINE Quelle fuer zwei Fragen: "gibt es ueberhaupt eine Stueckposition, die
 * Begehungen zaehlt?" (null = nein) und "wie viele sind darauf gebucht?".
 * Bewusst calculateStats und keine eigene Heuristik — genau diese Funktion
 * bestimmt den Vergleichswert der Regel 2 (Praefix 2.7./2.8., assignment
 * 'inspection' oder "begehung" im Titel). Eine zweite, engere Definition daneben
 * hiesse: der Guard schaltet Faelle ab, die die Regel sehr wohl prueft.
 *
 * calculateStats liegt zwar unter components/, ist aber eine reine Funktion ueber
 * ServiceItem — ohne React, DOM oder Datenbank. Der Import verletzt die oben
 * beschriebene Reinheit dieser Datei also nicht, und er stand hier schon fuer
 * die Regeln 2 und 4.
 */
const pieceInspectionStats = (services: ServiceItem[]) =>
    calculateStats(
        services.filter((s) => !isHourlyUnit(s.unit)),
        'inspection',
    );

/** Fuehrt das Projekt mindestens eine nicht-stuendliche Begehungsposition? */
const hasPieceInspectionPosition = (services: ServiceItem[]): boolean =>
    pieceInspectionStats(services) !== null;

/**
 * Das Projekt rechnet nach STUNDEN ab und kennt keine Begehungsposition.
 *
 * Dann ZAEHLT nichts im Projekt Begehungen: inspectionUsed und der Wert aus
 * calculateStats bleiben zwangslaeufig 0, waehrend jeder archivierte Bericht
 * die Abweichung um eins erhoeht. Die Regeln 1 und 2 melden dort nicht eine
 * fehlende Buchung, sondern nur, dass es keine Stueckposition gibt, auf die
 * gebucht werden koennte — Projekt 26031011 (rein nach Stunden abgerechnet)
 * lief ab zwei Berichten in Rot und konnte keine Rechnung mehr ausstellen.
 *
 * BEIDE Haelften sind noetig, die Stundenposition ist keine Zierde
 * (Entscheidung Betreiber 2026-08-24): ein Leistungsverzeichnis, das nur
 * Stueckpositionen ohne Begehungsbezug fuehrt (SiGe-Plan, Vorankuendigung) und
 * trotzdem Berichte archiviert, hat keine erklaerende Abrechnungsart — dort
 * bleibt die Abweichung ein Befund. Nur die Stundenabrechnung erklaert sie.
 *
 * Zwei weitere Lagen bleiben ausdruecklich scharf geprueft:
 *  - LEERES Leistungsverzeichnis: ein Projekt ohne jede Position, aber mit
 *    archivierten Berichten, ist eine echte Anomalie und kein Abrechnungsmodell.
 *  - GEMISCHT: sobald EINE Stueck-Begehungsposition existiert, traegt der
 *    Abgleich wieder, und genau dort hat er schon Buchungsverluste aufgedeckt.
 *
 * Der Guard muss dieselbe WEITE Begehungsdefinition benutzen wie die Regel, die
 * er abschaltet — sonst schaltet er mehr ab, als er erklaert. Mit dem engen
 * isInspectionService (nur 2.7.10, assignment oder "Begehung" im Titel) galt ein
 * Projekt mit 2.8.10 "Sicherheitstechnische Kontrolle" als reines Stundenprojekt,
 * obwohl Regel 2 diese Position ueber calculateStats sehr wohl zaehlt: zwei
 * erbrachte, archivierte Begehungen waeren stillschweigend gruen geworden.
 * Deshalb laeuft beides ueber pieceInspectionStats.
 */
const isHoursOnlyProject = (services: ServiceItem[]): boolean =>
    services.some((s) => isHourlyUnit(s.unit)) && !hasPieceInspectionPosition(services);

/**
 * Nennt die archivierten Berichte, zu denen keine Buchung im Protokoll steht.
 *
 * Die blosse Abweichung ("Archiv 16, Leistungsstand 15") sagt, DASS etwas
 * fehlt, und laesst den Anwender die Luecke von Hand suchen — bei 16 Berichten
 * ist das eine halbe Stunde Arbeit, und genau deshalb bleibt sie liegen, bis
 * sie in einer Rechnung auffaellt (Vorfall 26041014, Bericht #13).
 *
 * Genannt wird nur, wenn beide Zaehlungen uebereinstimmen: ergibt der Abgleich
 * ueber die Buchungsmerkmale exakt so viele unbebuchte Berichte wie die
 * Mengendifferenz, zeigen zwei unabhaengige Wege auf dieselben Berichte. Weicht
 * das ab (Altbestand ohne reportRef und ohne die alte Beschreibung, Buchungen
 * ohne Bericht), bleibt es bei der reinen Zahl statt einer falschen Liste.
 */
const namedGap = (
    reportHistory: Array<{ inspectionNumber?: number; date?: string; billingExempt?: boolean }>,
    serviceLog: ServiceTransaction[],
    delta: number,
): string => {
    const booked = bookedReportNumbers(serviceLog);
    const missing = reportHistory
        .filter((r) => r.inspectionNumber != null && !r.billingExempt && !booked.has(r.inspectionNumber))
        .sort((a, b) => (a.inspectionNumber ?? 0) - (b.inspectionNumber ?? 0));
    if (missing.length === 0 || missing.length !== delta) return '';
    const list = missing
        .map((r) => `#${r.inspectionNumber}${r.date ? ` vom ${formatDayDe(r.date)}` : ''}`)
        .join(', ');
    return ` Ohne Buchung: ${list}.`;
};

/** ISO-Tag als 31.07.2026; unbrauchbare Werte bleiben, wie sie sind. */
const formatDayDe = (iso: string): string => {
    const day = asDay(iso);
    if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return iso;
    const [y, m, d] = day.split('-');
    return `${d}.${m}.${y}`;
};

/** Monatsabstand zweier ISO-Daten, grob genug fuer eine Liegezeit-Warnung. */
const monthsBetween = (fromIso: string, toIso: string): number => {
    const a = new Date(`${fromIso}T00:00:00Z`);
    const b = new Date(`${toIso}T00:00:00Z`);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
    return (
        (b.getUTCFullYear() - a.getUTCFullYear()) * 12 +
        (b.getUTCMonth() - a.getUTCMonth()) +
        (b.getUTCDate() >= a.getUTCDate() ? 0 : -1)
    );
};

/** Ueberschneiden sich zwei Zeitraeume? Offene Enden zaehlen als unbegrenzt. */
const periodsOverlap = (aFrom?: string, aTo?: string, bFrom?: string, bTo?: string): boolean => {
    if (!aFrom && !aTo) return false;
    if (!bFrom && !bTo) return false;
    const aStart = aFrom ?? '0000-01-01';
    const aEnd = aTo ?? '9999-12-31';
    const bStart = bFrom ?? '0000-01-01';
    const bEnd = bTo ?? '9999-12-31';
    return aStart <= bEnd && bStart <= aEnd;
};

// ═══════════════════════════════════════════════════════════════════════════
// Der Check
// ═══════════════════════════════════════════════════════════════════════════

export const runProjectCheck = (
    input: ProjectCheckInput,
    todayIso: string,
    config: ProjectCheckConfig = DEFAULTS,
): ProjectCheckResult => {
    const findings: ProjectCheckFinding[] = [];

    // traffic 'green' ist die HINWEISZEILE: sie erscheint in der Liste, faerbt
    // die Ampel aber nicht (die Auswertung unten fragt nur nach Rot und Gelb)
    // und sperrt nichts. Damit laesst sich eine Zahl mitteilen, ohne sie zu
    // einem Befund aufzublasen — bisher gab es dafuer keinen Platz.
    const add = (
        rule: ProjectCheckRule,
        traffic: ProjectCheckTraffic,
        title: string,
        detail: string,
        extra: Partial<ProjectCheckFinding> = {},
    ) => {
        findings.push({
            id: `${input.projectId}_${rule}${extra.serviceId ? `_${extra.serviceId}` : ''}`,
            rule,
            type: rule,
            title,
            detail,
            severity: SEVERITY[traffic],
            traffic,
            projectId: input.projectId,
            projectName: input.projectName,
            // Rot laesst sich nicht wegquittieren. Wer es trotzdem muss, stellt
            // blockOnRed ab — das ist eine bewusste Konfigurationsentscheidung
            // und keine Nebenwirkung eines Klicks im Rechnungsdialog.
            acknowledgeable: traffic !== 'red',
            ...extra,
        });
    };

    const services = input.services || [];
    const log = input.serviceLog || [];
    const reservations = input.reservations ?? new Map<string, number>();
    // Gilt nur fuer die Regeln 1 und 2 — sie sind die einzigen, die Berichte
    // gegen einen Begehungszaehler halten. Alle uebrigen Regeln rechnen mit
    // Mengen und Betraegen und bleiben davon unberuehrt.
    const hoursOnly = isHoursOnlyProject(services);
    // Eigene Berichte, die als Nachweiszeile auf der Pauschale stehen (Menge 0,
    // 'NW-…'). Im Pauschalprojekt waechst die Begehungsposition seit der
    // Umstellung nicht mehr — ohne diesen Anteil liefen die Regeln 1 und 2 ab
    // dem ersten Nachweis auseinander und meldeten gebuchte Berichte als
    // ungebucht. hoursOnly bleibt davon bewusst unberuehrt: ein Stundenprojekt,
    // das erst seit der Umstellung Nachweise fuehrt, hielte sonst sein ganzes
    // Archiv gegen die wenigen Nachweise.
    //
    // NUR eigene Berichte (nicht inspectionProofCount): beide Regeln halten
    // Buchungen gegen Archiv und Zaehler DIESES Projekts. Ein Tochterbericht
    // ('RPT-CHILD-…') liegt im Archiv der Tochter und traegt deren Nummer, eine
    // Hand-Begehung ('NW-BEG-…') hat weder Bericht noch Nummer. Mitgezaehlt
    // deckten sie einen eigenen Bericht ohne Buchung zu.
    const proofInspections = ownReportProofCount(services, log);

    // ── 12. Kein Kunde ────────────────────────────────────────────────────
    if (!input.customerId) {
        add(
            'customer_missing',
            'red',
            'Kein Kunde am Projekt',
            'Dem Projekt ist kein Kunde zugeordnet. Ohne Rechnungsempfänger kann keine Rechnung nach §14 UStG entstehen.',
        );
    }

    // ── 11. Unterprojekt ──────────────────────────────────────────────────
    if (input.linkedProjectConfig?.role === 'child') {
        add(
            'child_project_direct',
            'red',
            'Unterprojekt wird nicht direkt abgerechnet',
            `Das Leistungsverzeichnis liegt am übergeordneten Projekt "${input.linkedProjectConfig.linkedProjectName}". Eine Rechnung hier würde dieselben Mengen ein zweites Mal abrechnen.`,
        );
    }

    // ── 10. Schlussrechnung bei laufendem Projekt ─────────────────────────
    // Bei SiFa entfaellt die Regel: eine Dauerbetreuung wird nie `finished`,
    // sie wuerde die Schlussrechnung fuer immer sperren.
    // 'completed' (BEENDET) ist der Regelfall fuer die Schlussrechnung, 'finished'
    // (ARCHIV) der Nachzuegler — beide sind abgeschlossen.
    if (
        (input.documentType === 'FINAL_INVOICE' || input.documentType === 'REMAINING_INVOICE') &&
        input.projectStatus &&
        input.projectStatus !== 'finished' &&
        input.projectStatus !== 'completed' &&
        !input.sifa
    ) {
        add(
            'project_not_finished',
            'red',
            'Schlussrechnung bei nicht abgeschlossenem Projekt',
            `Das Projekt steht auf "${input.projectStatus}". Eine Schlussrechnung schließt die Abrechnung ab — spätere Leistungen wären nur noch über eine Nachtragsrechnung abrechenbar.`,
        );
    }

    // ── 1. Berichte im Archiv gegen gebuchten Leistungsstand ──────────────
    // Die Regel setzt voraus, dass jede erbrachte Begehung einen archivierten
    // Bericht hat. Fuer Projekte ohne Berichtsfuehrung gilt das nicht — dort
    // ist das leere Archiv die Arbeitsweise und kein Befund.
    //
    // Nur EINE Richtung ist ein Befund: mehr Berichte im Archiv als
    // Leistungsstand. Dann fehlt zu einem geschriebenen Bericht die Buchung,
    // und die Rechnung liesse erbrachte Leistung liegen.
    //
    // Der umgekehrte Fall — Leistungsstand hoeher als das Archiv — ist Alltag
    // und ausdruecklich gruen (Entscheidung Betreiber 2026-08-13): abgerechnet
    // wird die erbrachte Leistung, nicht die Zahl der Berichte. Berichte des
    // Auftraggebers, nachgetragene Begehungen und Leistungen ohne eigenen
    // Bericht erzeugten sonst bei jeder Rechnung eine Kenntnisnahmepflicht.
    //
    // Bei reiner Stundenabrechnung entfaellt die Regel ganz: dort gibt es
    // keinen Leistungsstand in Begehungen, gegen den das Archiv laufen koennte
    // (siehe isHoursOnlyProject).
    if (
        config.enableReportCount &&
        input.reportHistory &&
        !input.reportExempt &&
        !hoursOnly
    ) {
        // Ausdruecklich nicht abrechenbare Berichte erwarten keine Buchung.
        // Ohne solche Berichte bleiben Zahl und Text wie bisher — der
        // Fingerabdruck quittierter Befunde haengt daran.
        const exempt = input.reportHistory.filter((r) => r.billingExempt === true).length;
        const archived = input.reportHistory.length - exempt;
        const inspectionUsed =
            services.filter(isInspectionService).reduce((sum, s) => sum + (s.quantityUsed || 0), 0) +
            proofInspections;
        const delta = archived - inspectionUsed;
        if (delta > 0) {
            const red = delta >= (config.reportCountRedDelta || 2);
            add(
                'report_count_mismatch',
                red ? 'red' : 'yellow',
                'Berichte und Leistungsstand weichen ab',
                `Archiv: ${archived} ${archived === 1 ? 'Bericht' : 'Berichte'}${
                    exempt > 0 ? ` (ohne ${exempt} nicht abrechenbare)` : ''
                }, Leistungsstand: ${inspectionUsed}. Abweichung: ${delta}.`
                    + namedGap(input.reportHistory, input.serviceLog, delta),
                { expected: archived, actual: inspectionUsed },
            );
        }
    }

    // ── 2. Begehungszaehler gegen Archiv und Leistungsstand ───────────────
    //
    // Drei Quellen fuer dieselbe Zahl, die auseinanderlaufen koennen: der
    // Zaehler an den Metadaten, die Zahl archivierter Berichte und die Summe
    // im Leistungsverzeichnis. Die Abweichung ist eine bekannte Schwaeche der
    // Oberflaeche; sie soll vor einer Rechnung sichtbar sein — aber nur, soweit
    // sie Geld betrifft.
    // Dieselbe Ausnahme wie oben: ohne Berichtsfuehrung ist das Archiv leer,
    // und der Dreiervergleich meldet nur diesen Umstand.
    //
    // Die Regel hat zwei Haelften, und sie greifen einander ausschliessend
    // (Begruendung ausfuehrlich weiter unten, bei `counterVsArchive`):
    //
    //   · Das Leistungsverzeichnis bleibt zurueck (`lvBehind`) — erbrachte
    //     Leistung ist nicht gebucht, es liegt eine Forderung still. Das ist
    //     der Geldbefund und bleibt in jeder Lage scharf.
    //   · Zaehler und Archiv laufen auseinander — eine Aussage ueber die
    //     BERICHTSFUEHRUNG, nicht ueber Geld. Sie wird nur noch dort gemeldet,
    //     wo das Leistungsverzeichnis die Geldfrage gar nicht beantworten kann:
    //     bei reiner Stundenabrechnung bleibt sein Wert zwangslaeufig 0, also
    //     sagt "LV bleibt zurueck" dort nichts, und der Zaehler/Archiv-Vergleich
    //     ist das einzige Signal — ein Stundenprojekt mit Zaehler 9 und drei
    //     Berichten im Archiv hat fuenf fehlende Berichte, ganz gleich, wie
    //     abgerechnet wird.
    if (
        input.currentInspectionNumber != null &&
        input.reportHistory &&
        !input.reportExempt
    ) {
        // Der Zaehler traegt die Nummer der AKTUELLEN Begehung — ob sie schon
        // archiviert ist, sagt er nicht. Das pauschale "minus eins" unterstellte
        // den Normalfall (ein Bericht ist offen und traegt bereits die neue
        // Nummer, das Archiv hinkt genau eine Nummer hinterher) und erzeugte
        // sonst selbst die Abweichung, die die Regel aufdecken soll: Projekt
        // 26031011 hatte Zaehler 14 UND Bericht 14 im Archiv und lief trotzdem
        // gelb. Entschieden wird deshalb ueber die HOECHSTE archivierte Nummer:
        //   gleich dem Zaehler      → kein Bericht offen, erledigt sind so viele,
        //                             wie der Zaehler nennt
        //   gleich Zaehler minus 1  → der offene Bericht traegt die aktuelle
        //                             Nummer, erledigt sind Zaehler minus 1
        //   tiefer oder gar keine   → die Luecke ist echt, es bleibt bei
        //                             Zaehler minus 1 und die Regel feuert
        const counter = input.currentInspectionNumber;
        // Fehlende, leere oder unbrauchbare Nummern bleiben draussen, sonst
        // faerbte ein einziger Altbestandseintrag das Maximum auf NaN.
        const archivedNumbers = input.reportHistory
            .map((r) => r.inspectionNumber)
            .filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
        const highestArchived = archivedNumbers.length ? Math.max(...archivedNumbers) : null;
        const fromCounter = Math.max(0, highestArchived === counter ? counter : counter - 1);
        const fromArchive = input.reportHistory.length;
        // Dieselbe Ausnahme wie oben: calculateStats zaehlt alles unter 2.7./2.8.
        // zusammen, also auch die Stundenposition. Der Zaehler zaehlt Begehungen.
        // Die Nachweise eigener Berichte auf der Pauschale sind gebuchte
        // Begehungen wie die Menge der Position — der Versand hat fuer jeden
        // von ihnen den Zaehler hochgezaehlt (proofInspections, oben).
        const fromLv = (pieceInspectionStats(services)?.used ?? 0) + proofInspections;
        // Wie in Regel 1: ein Leistungsstand ueber Zaehler und Archiv ist gruen.
        // Auffaellig bleibt, wenn das Leistungsverzeichnis HINTER einer der
        // beiden Quellen zurueckbleibt — dann ist erbrachte Leistung nicht
        // gebucht, und genau das kostet Geld.
        //
        // Ausdruecklich nicht abrechenbare Berichte belegen eine Nummer und
        // eine Archivzeile, aber keine Leistung: sie gehen von der Erwartung ab.
        // Nur fuer die Geldfrage — der Zaehler/Archiv-Vergleich bei
        // Stundenabrechnung (Berichtsfuehrung) zaehlt sie weiter mit.
        const exemptReports = input.reportHistory.filter((r) => r.billingExempt === true).length;
        const expectedLv = Math.max(fromCounter, fromArchive) - exemptReports;
        const lvBehind = !hoursOnly && fromLv < expectedLv;
        //
        // ── Warum der reine Zaehler/Archiv-Vergleich nur noch bei
        //    Stundenabrechnung feuert ────────────────────────────────────────
        //
        // Bis 2026-08-30 genuegte `fromCounter !== fromArchive`, um zu melden.
        // Auf PROD standen daraufhin 202 offene Befunde dieser Regel, und der
        // grosse Teil davon war Rauschen. Der Betreiber nannte drei Klassen:
        //
        //  1. ALTBESTAND OHNE ARCHIV — bei der Datenuebernahme wurde die Nummer
        //     der naechsten Begehung uebernommen, das Berichtsarchiv nicht.
        //     Typisch: "Zaehler: 113, Archiv: 16, Leistungsverzeichnis: 113".
        //  2. MUTTERPROJEKTE — die Toechter buchen ihre Leistung auf die Mutter
        //     (`RPT-CHILD-…` im Protokoll der Mutter), ihre BERICHTE liegen im
        //     Archiv der Tochter. Das Leistungsverzeichnis der Mutter zaehlt sie
        //     also mit, ihr Archiv nie.
        //  3. LEISTUNG OHNE BERICHT — bewusst nur gebucht, nie geschrieben.
        //
        // EINE Bedingung statt dreier Sonderfaelle, und zwar nicht aus
        // Sparsamkeit: alle drei Klassen haben DIESELBE Gestalt. In allen dreien
        // ist das Leistungsverzeichnis vollstaendig (Klasse 1: gleichauf mit dem
        // Zaehler; Klassen 2 und 3: darueber), und nur die Berichtsfuehrung
        // hinkt. Nach dem Massstab dieser Regel — Geld liegt still, wenn
        // erbrachte Leistung NICHT GEBUCHT ist — sagt eine solche Abweichung
        // nichts ueber Geld. Drei Ausnahmen nebeneinander waeren drei Dinge, die
        // spaeter einzeln zu pflegen sind; eine Ausnahme, die aus dem Massstab
        // folgt, traegt auch die vierte Klasse, die noch niemand gemeldet hat.
        //
        // Damit ist `fromCounter !== fromArchive` fuer jedes Projekt, dessen
        // Leistungsverzeichnis Begehungen zaehlen KANN, vollstaendig redundant:
        // laeuft es mit, ist nichts zu holen; bleibt es zurueck, feuert
        // `lvBehind` ohnehin. Uebrig bleibt der Fall, in dem das
        // Leistungsverzeichnis die Geldfrage gar nicht beantworten kann — die
        // reine Stundenabrechnung. Dort ist der Vergleich von Zaehler und Archiv
        // das einzige Signal und bleibt unveraendert scharf (Vorfall 26031011:
        // Zaehler 9 gegen drei Berichte heisst fuenf fehlende Berichte, ganz
        // gleich wie abgerechnet wird).
        //
        // ZU KLASSE 1, "Abweichung groesser 5": als Faustregel bewusst NICHT
        // uebernommen. Sie trennt nach der GROESSE der Abweichung, der Massstab
        // trennt nach ihrer RICHTUNG. Eine Luecke von 6 bei zurueckbleibendem
        // Leistungsverzeichnis waere unter der Faustregel still — das ist genau
        // der teure Fall. Das Muster ist praeziser fassbar und wird hier so
        // gefasst: das Leistungsverzeichnis laeuft mit dem Zaehler mit, nur das
        // Archiv hinkt (`fromLv >= fromCounter`). Das deckt das Beispiel des
        // Betreibers exakt und macht die Faustregel entbehrlich, statt sie als
        // zweite, gegenlaeufige Wahrheit danebenzustellen.
        //
        // ZU KLASSE 2, woran die Mutter festgemacht wird: an KEINEM von beiden,
        // weder an `linkedProjectConfig.role === 'parent'` noch an den
        // `RPT-CHILD-`-Buchungen in `input.serviceLog` — beides steht hier zur
        // Verfuegung und wird bewusst nicht gebraucht. Die Rolle waere zu grob:
        // eine Mutter ohne Tochterbuchungen hat vollstaendig vergleichbare
        // Zahlen und verloere die Pruefung umsonst. Die Buchungen waeren
        // richtig, aber ueberfluessig: eine Tochterbuchung hebt IMMER nur das
        // Leistungsverzeichnis der Mutter (die Archivzeile liegt in der
        // Tochter), verschiebt die Lage also stets in die Richtung, die schon
        // gruen ist. Bleibt das Leistungsverzeichnis der Mutter trotz aller
        // Tochterbuchungen hinter ihrem EIGENEN Zaehler zurueck, fehlt eine
        // eigene Buchung der Mutter — das muss melden. Ob die Begehung der
        // Tochter selbst gebucht wurde, prueft nicht diese Regel, sondern der
        // eigene Weg fuer versendete Berichte ohne Buchung
        // (`unbookedReportsLogic`), der den Mutter/Tochter-Zweig kennt.
        //
        // ZU KLASSE 3: dafuer gibt es den Schalter `reportExempt` ("Keine
        // Begehungen/Berichte fuer dieses Projekt"), der beide Regeln abschaltet
        // und ausserdem die Erinnerungen stilllegt. Ein zweiter Sonderfall im
        // Code waere eine halbe Kopie davon. Nach dieser Aenderung schweigt die
        // Regel dort ohnehin, weil das Leistungsverzeichnis ueber dem Archiv
        // liegt; der Haken bleibt trotzdem das richtige Mittel fuer solche
        // Projekte, weil er mehr abschaltet als diese eine Regel.
        //
        // WAS DADURCH NICHT MEHR AUFFAELLT — und das ist der Preis:
        //  · Ein Bericht, der geschrieben, aber nie archiviert wurde, faellt
        //    dieser Regel nicht mehr auf, solange seine Leistung gebucht ist.
        //    Die Regel sagt nichts mehr ueber die Vollstaendigkeit der
        //    BERICHTSFUEHRUNG, nur noch ueber die der BUCHUNG.
        //  · Ein verstellter Begehungszaehler (zu hoch gesetzt, doppelt
        //    hochgezaehlt) bleibt unbemerkt, solange das Leistungsverzeichnis
        //    mitlaeuft. Die naechste Begehung bekommt dann eine Nummer, die es
        //    im Archiv nie gab.
        //  · Beides ist kein Geldbefund und war auf PROD in 202 Meldungen so
        //    verduennt, dass es ohnehin niemand mehr las.
        const counterVsArchive = hoursOnly && fromCounter !== fromArchive;
        if (lvBehind || counterVsArchive) {
            const gap = expectedLv - fromLv;
            const quelle =
                fromCounter === fromArchive
                    ? 'Zähler und Archiv'
                    : fromCounter > fromArchive
                      ? 'dem Zähler'
                      : 'dem Archiv';
            const berichtsluecke = fromCounter - fromArchive;
            // Der Befundtext muss nach der Entschaerfung sagen, warum diese
            // Meldung noch etwas bedeutet — sonst liest sie sich wie die 202,
            // die sie ersetzt.
            const grund = lvBehind
                ? ` Das Leistungsverzeichnis bleibt hinter ${quelle} zurück: ${gap} erbrachte ${
                      gap === 1 ? 'Begehung ist' : 'Begehungen sind'
                  } nicht gebucht und blieben unberechnet.`
                : ' Das Projekt rechnet nach Stunden ab; das Leistungsverzeichnis zählt hier keine Begehungen.' +
                  (berichtsluecke > 0
                      ? ` Zu ${berichtsluecke} gezählten ${
                            berichtsluecke === 1 ? 'Begehung' : 'Begehungen'
                        } fehlt der archivierte Bericht.`
                      : ` Das Archiv führt ${-berichtsluecke} ${
                            -berichtsluecke === 1 ? 'Bericht' : 'Berichte'
                        } mehr, als der Zähler kennt.`);
            add(
                'inspection_counter_mismatch',
                'yellow',
                'Begehungszähler uneinheitlich',
                `Zähler: ${fromCounter}, Archiv: ${fromArchive}, Leistungsverzeichnis: ${fromLv}.` +
                    (exemptReports > 0 && lvBehind
                        ? ` Davon ausdrücklich nicht abrechenbar: ${exemptReports}.`
                        : '') +
                    grund,
            );
        }
    }

    // ── 4. Vorankuendigung: Historie gegen Leistungsstand ─────────────────
    if (config.enableVorankHistory && input.vorankuendigung) {
        const history = input.vorankuendigung.history?.length ?? 0;
        const used = calculateStats(services, 'vorank')?.used ?? 0;
        if (history !== used) {
            add(
                'vorank_history_mismatch',
                'yellow',
                'Vorankündigung: Historie und Leistungsstand weichen ab',
                `Erstellt laut Historie: ${history}, gebucht im Leistungsverzeichnis: ${used}.`,
                { expected: history, actual: used },
            );
        }
    }

    // ── 9. Fehlende Startdokumente ────────────────────────────────────────
    //
    // Wortgleich zur Regel 3 aus AdminAlertsView, damit beide Ansichten
    // dasselbe melden und nicht zwei Wahrheiten entstehen.
    // Bei SiFa entfaellt die Regel: Vorankuendigung und Unterlage sind
    // Baustellendokumente, eine Betreuung hat sie nicht.
    if (config.enableStartDocs && input.startDate && input.startDate <= todayIso && !input.sifa) {
        const find = (kind: 'vorank' | 'unterlage') =>
            services.find((s) =>
                kind === 'vorank'
                    ? s.lvPosition === '2.1.10' ||
                      s.assignment === 'vorank' ||
                      (s.title || '').toLowerCase().includes('vorankündigung')
                    : s.lvPosition === '1.4.10' ||
                      s.assignment === 'unterlage' ||
                      (s.title || '').toLowerCase().includes('unterlage'),
            );
        const missing = (s?: ServiceItem) =>
            !!s && (s.quantityUsed || 0) === 0 && s.status !== 'done' && s.status !== 'billed';

        const docs: string[] = [];
        if (missing(find('vorank'))) docs.push('Vorankündigung');
        if (missing(find('unterlage'))) docs.push('Unterlage');
        if (docs.length) {
            add(
                'missing_start_docs',
                'yellow',
                'Startdokumente fehlen',
                `Seit Projektbeginn (${input.startDate}) nicht erstellt: ${docs.join(', ')}.`,
            );
        }
    }

    // ── Paketposition: Unterlage als Bestandteil UND als eigene Position ──
    //
    // Betreiberentscheid E9 (19.10.2026): erlaubt, aber sichtbar. Steht die
    // Vorankuendigung sowohl im Paket „Planungsphase" als auch als eigene
    // LV-Position, bucht das Modul nach der Freigabe beides an (Plan B5,
    // Fall 3) — ob der Auftraggeber die Unterlage wirklich zweimal bezahlt,
    // muss ein Mensch sehen. Geprueft ueber die ZUORDNUNG der anderen
    // Position (bestandteilLogic.zuordnungFuerQuelle), nie ueber ein Titelwort.
    //
    // GRUEN, also eine Hinweiszeile wie `open_retention` (Controller-Entscheid
    // 19.10.2026): als Gelb fiele das ganze Projekt ohne „Gelbe einbeziehen"
    // aus dem Rechnungslauf, und die uebrigen Positionen blieben liegen — fuer
    // eine Lage, die zulaessig ist. Der Integritaetswaechter uebernimmt sie
    // nicht (integrityLogic.NICHT_UEBERNOMMEN), kein naechtliches Rauschen.
    for (const paket of services) {
        if (!hatBestandteile(paket)) continue;
        const doppelt: string[] = [];
        for (const b of normalisiereBestandteile(paket.bestandteile) ?? []) {
            const zuordnung = zuordnungFuerQuelle(b.quelle);
            if (!zuordnung) continue;
            const eigene = services.filter((x) => x.id !== paket.id && x.assignment === zuordnung);
            if (eigene.length === 0) continue;
            doppelt.push(
                `${b.label} (auch ${eigene.map((x) => x.lvPosition || x.title).join(', ')})`,
            );
        }
        if (doppelt.length > 0) {
            add(
                'bestandteil_doppelt_als_position',
                'green',
                `Unterlage doppelt im LV (${paket.lvPosition || paket.title})`,
                `Bestandteil der Paketposition und zugleich eigene Position: ${doppelt.join('; ')}. ` +
                    'Zulässig, aber bitte prüfen, dass die Unterlage nicht zweimal abgerechnet wird.',
                { serviceId: paket.id, lvPosition: paket.lvPosition },
            );
        }
    }

    // ── Regeln je Leistungsposition ───────────────────────────────────────
    let anyOpen = false;
    // PAKETPOSITION: erbracht, aber zurueckgehalten — fuer den Text von
    // `no_open_positions` (dort ist „nichts offen" sonst missverstaendlich).
    let anyHeld = false;
    for (const s of services) {
        // NEP: beauftragt ist, was gebucht ist — kein Ueberschreitungs-Hinweis
        // gegen einen Vordersatz, den nie jemand bestellt hat.
        const contracted = contractedQuantity(s);
        const used = s.quantityUsed || 0;
        const billed = s.quantityBilled || 0;
        const reserved = reservations.get(s.id) || 0;
        const open = openQuantity(s, reserved);
        if (open > 0) anyOpen = true;

        const where = { serviceId: s.id, lvPosition: s.lvPosition };

        // ── Paketposition (docs/positionsbestandteile-plan.md, B2) ─────────
        if (hatBestandteile(s)) {
            const gate = bestandteilGate(s);
            // 20. Alle Bestandteile erledigt, Position aber nicht gebucht.
            //
            // Die automatische Positionsbuchung (E1) laeuft an der
            // ausloesenden Handlung, das Netz ist der Nachtlauf. Steht der
            // Befund nach einer Nacht noch, hat keiner von beiden gebucht —
            // und das Geld liegt herum, ohne dass eine Rechnung es je
            // anbietet (openQuantity sieht `quantityUsed`, nicht die
            // Bestandteile). Gelb, weil hier Geld FEHLT — die Buchung ist ein
            // Handgriff, aber ohne ihn kommt die Position auf keinen Beleg.
            // Der Integritaetswaechter uebernimmt sie bewusst (Nachtlauf-Netz).
            if (gate === 'frei' && used < contracted - BOOKING_EPSILON) {
                add(
                    'bestandteile_vollstaendig_nicht_gebucht',
                    'yellow',
                    `Bestandteile vollständig, Position nicht gebucht (${s.lvPosition || s.title})`,
                    `${bestandteilSperrText(s)} erledigt, gebucht sind ${used} von ${contracted} ${s.unit || ''}. ` +
                        'Die Position muss noch gebucht werden, sonst erscheint sie auf keiner Rechnung.',
                    { ...where, expected: contracted, actual: used },
                );
            }
            // 21. Position gebucht, Bestandteile aber offen (oder unbekannt).
            //
            // Die gebuchte Menge ist ZURUECKGEHALTEN (openQuantity 0): sie
            // steht auf keinem Beleg, bis die Bestandteile erledigt sind.
            // Ein Arbeitsstand (gebucht von Hand vor dem letzten Bestandteil,
            // Altbestand, nachtraeglich geaenderte Liste) — sichtbar muss er
            // trotzdem sein, sonst wartet Geld auf einen Bestandteil, an den
            // niemand mehr denkt.
            //
            // GRUEN, eine Hinweiszeile (Controller-Entscheid 19.10.2026): die
            // Position selbst ist ueber das Gate ohnehin gesperrt. Als Gelb
            // fiele das GANZE Projekt ohne „Gelbe einbeziehen" aus dem
            // Rechnungslauf, und die uebrigen, abrechenbaren Positionen
            // blieben liegen. Kein Eintrag im Integritaetswaechter
            // (integrityLogic.NICHT_UEBERNOMMEN).
            const zurueck = used - billed - waivedQuantity(s);
            if (istAbrechnungGesperrt(s) && zurueck > BOOKING_EPSILON) {
                anyHeld = true;
                add(
                    'position_gebucht_bestandteile_offen',
                    'green',
                    `Position gebucht, Bestandteile offen (${s.lvPosition || s.title})`,
                    `Gebucht ${used}, abgerechnet ${billed}: ${Math.round(zurueck * 1000) / 1000} ${s.unit || ''} ` +
                        `zurückgehalten — ${bestandteilSperrText(s)}. Erst abrechenbar, wenn alle Bestandteile erledigt sind.`,
                    { ...where, expected: billed, actual: used },
                );
            }
        }

        // 3. Vertragsmenge ueberschritten
        if (config.enableContractOverrun && contracted > 0 && used > contracted) {
            // IMMER nur ein Hinweis, nie eine Sperre (Entscheidung 2026-08-11):
            // die Mehrmenge ist erbrachte Leistung, und ob sie abrechenbar ist,
            // klaert der Betreiber mit dem Auftraggeber — eine Rechnung darf
            // daran nicht scheitern. Die Nachtragsnotiz bleibt als Kontext im
            // Text erwaehnt.
            const hasSupplement = (input.notes || []).some((n) =>
                /nachtrag|beauftrag/i.test(n.text || ''),
            );
            add(
                'contract_overrun',
                'yellow',
                `Vertragsmenge überschritten (${s.lvPosition || s.title})`,
                `Beauftragt: ${contracted}, erbracht: ${used}. Mehrmenge: ${Math.round((used - contracted) * 1000) / 1000}.${
                    hasSupplement ? ' Es liegt eine Nachtragsnotiz vor.' : ' Kein Nachtrag vermerkt.'
                }`,
                { ...where, expected: contracted, actual: used },
            );
        }

        // 6. Mehr abgerechnet als erbracht
        //
        // Mit derselben Toleranz, mit der gebucht wird: eine exakt geteilte
        // Menge landet ueber drei Teilbelege ein Vielfaches von 1e-17 neben
        // der erbrachten Menge. Das ist Gleitkommastaub, kein Geld — eine
        // echte Ueberabrechnung ist mindestens ein Tausendstel gross.
        if (billed > used + BOOKING_EPSILON) {
            add(
                'billed_exceeds_used',
                'red',
                `Mehr abgerechnet als erbracht (${s.lvPosition || s.title})`,
                `Abgerechnet: ${billed}, erbracht: ${used}.`,
                { ...where, expected: used, actual: billed },
            );
        }

        // 19. Abrechnungszaehler unter der Rueckschreibung des Protokolls
        //
        // Vorfall 26051026 (19.09.2026): die Schlussrechnung hatte 4 Stueck
        // gefordert und zurueckgeschrieben, eine alte Sitzung schrieb den
        // Zaehler danach auf 1 zurueck. Die Position stand wieder mit 3
        // „offen" — die naechste fortschreibende Rechnung haette sie ein
        // zweites Mal gefordert. Keine Regel sah es; aufgefallen ist es dem
        // Betreiber beim Blick in die Abrechnung.
        //
        // Rot, weil die Folge eine falsche Geldforderung ist — dieselbe
        // Begruendung wie bei `billed_exceeds_used`. Gerechnet wird ueber die
        // Rueckschreibungen im Protokoll an der REFERENZ (postedQuantity:
        // BU-/MB-/VZ-/RN-, Rechnung +, Storno −; kein Textzweig, ALT-Zeilen
        // nicht — Begruendung dort), mit derselben Toleranz wie beim Buchen.
        // Die Gegenrichtung (Zaehler UEBER der Summe) ist Altbestand mit
        // manueller Verbuchung ohne Zeile (Vorfall 6172) und bewusst kein
        // Befund. Der Server haelt den Zaehler seit Nr. 111 mit demselben
        // Praedikat — diese Regel meldet, was davor verloren ging oder am
        // Riegel vorbeikam.
        //
        // Liegt die Rueckschreibung UEBER dem Leistungsstand, kann sie nicht
        // der Abrechnungsstand sein — abgerechnet wird nie mehr als erbracht.
        // Dann beschreiben alte Zeilen („Manuelle Verbuchung durch Admin",
        // BU-JJJJ-NNNNN) einen Stand, den spaetere Korrekturen ohne Minuszeile
        // gesenkt haben (PROD 25.09.2026: 6051 2.7.20 erbracht 54 / Summe 124,
        // 6172 3.4.10 erbracht 41,25 / Summe 47,75). Die Summe taugt dort
        // nicht als Massstab, und Rot sperrte jede Rechnung des Projekts.
        // Der Preis (Codex P1, vom Betreiber angenommen): dort bliebe auch ein
        // echtes Zuruecksetzen still. Solche Positionen werden deshalb per
        // Ausgleichszeile bereinigt (fix_6051_6172_rueckschreibung_abgleich),
        // die Grenze ist nur das Sicherheitsnetz fuer kuenftige Faelle.
        const posted = postedQuantity(log, s.id);
        if (posted > billed + BOOKING_EPSILON && posted <= used + BOOKING_EPSILON) {
            add(
                'billed_below_postings',
                'red',
                `Abrechnungsstand unter der Rückschreibung (${s.lvPosition || s.title})`,
                `Abgerechnet laut Zähler: ${billed}, laut Rechnungs-Rückschreibungen im Protokoll: ${posted}. Fehlt im Zähler: ${Math.round((posted - billed) * 1000) / 1000}. Die nächste Rechnung würde die Menge erneut fordern.`,
                { ...where, expected: posted, actual: billed },
            );
        }

        // 7. Menge bereits von einem anderen Entwurf beansprucht
        if (reserved > 0 && used - billed - reserved < -BOOKING_EPSILON) {
            add(
                'reserved_exceeds_open',
                'red',
                `Menge doppelt beansprucht (${s.lvPosition || s.title})`,
                `Offen wären ${used - billed}, ein anderer Entwurf beansprucht bereits ${reserved}.`,
                { ...where, expected: used - billed, actual: reserved },
            );
        }

        // 8. Offene Menge ohne Preis
        if (open > 0 && (!s.unitPrice || s.unitPrice === 0)) {
            add(
                'zero_unit_price',
                'yellow',
                `Kein Einheitspreis (${s.lvPosition || s.title})`,
                `Offene Menge ${open} ${s.unit || ''} zum Einzelpreis 0,00 €. Die Position erschiene mit Betrag 0 auf der Rechnung.`,
                where,
            );
        }

        // 5. Leistungsstand und Protokoll weichen ab — in BEIDE Richtungen.
        //
        // Faengt den Pfad, auf dem quantityUsed direkt gesetzt wird (etwa bei
        // einer Leistungsstandkorrektur), ohne dass ein Arbeitsnachweis
        // entsteht. Buchungseintraege zaehlen nicht mit — sie beschreiben die
        // Abrechnung, nicht die Leistung.
        if (config.enableUnloggedQuantity) {
            // Dieselbe Summe, die der Riegel am Beleg zieht (serviceLogUtils:
            // loggedQuantity) — zwei eigene Rechnungen liefen frueher oder
            // spaeter auseinander, und dann meldete die Projektpruefung etwas
            // anderes, als die Rechnung sperrt.
            const logged = loggedQuantity(log, s.id);
            const delta = used - logged;
            // Die andere Richtung: im Protokoll steht MEHR, als gebucht ist.
            //
            // Bis hierher war nur der Fall sichtbar, dass eine Menge ohne
            // Nachweis gebucht wurde. Der umgekehrte kostet Geld: die Leistung
            // ist erbracht und protokolliert, der Leistungsstand hinkt
            // hinterher, und abgerechnet wird nur, was im Leistungsstand steht.
            // Genau so blieben in Projekt 6016 sechs Stunden unberechnet,
            // nachdem der Nachtragsdialog den Stundenstand auf den
            // Begehungszaehler gesetzt hatte.
            //
            // Gerechnet wird ueber DIESELBE Summe wie oben, mit Vorzeichen:
            // eine Korrektur nach unten steht als negativer Eintrag im
            // Protokoll und senkt sie. Nur die positiven Eintraege zu zaehlen
            // meldete jede zurueckgenommene Menge als Fehlbetrag.
            if (delta < 0) {
                add(
                    'unbilled_logged_quantity',
                    'yellow',
                    `Leistungsstand niedriger als Protokollsumme (${s.lvPosition || s.title})`,
                    `Protokolliert: ${logged}, gebucht: ${used} (${used} von ${logged} erfasst). Nicht im Leistungsstand: ${-delta}.`,
                    { ...where, expected: logged, actual: used },
                );
            }
            if (delta > 0) {
                // Wie contract_overrun IMMER nur ein Hinweis, nie eine Sperre
                // (Entscheidung 2026-08-12): die Menge IST erbracht und
                // gebucht, es fehlt nur die Protokollspur — typisch bei
                // Altbestaenden mit direkt gesetztem Leistungsstand. Eine
                // Rechnung darf daran nicht scheitern; die Luecke bleibt als
                // quittierbarer Befund sichtbar.
                add(
                    'unlogged_quantity',
                    'yellow',
                    `Leistung ohne Nachweis (${s.lvPosition || s.title})`,
                    `Gebucht: ${used}, durch Einträge belegt: ${logged}. Ohne Nachweis: ${delta}.`,
                    { ...where, expected: logged, actual: used },
                );
            }
        }

        // 14. Liegengebliebene offene Position
        // Bei SiFa entfaellt die Regel: eine Betreuung ruft Leistungen ohne
        // festen Rhythmus ab, Monate ohne Bewegung sind dort kein Befund.
        if (config.enableStalePositions && open > 0 && !input.sifa) {
            // Buchungseintraege zaehlen nicht als Bewegung: sie sind die
            // Rueckschreibung einer Rechnung, keine Leistung. Ohne diesen
            // Filter setzte ausgerechnet das Abrechnen die Liegezeit zurueck —
            // eine Position, die seit einem Jahr ruht, saehe frisch aus, sobald
            // ein Teil von ihr berechnet wurde.
            // Auf den Kalendertag kuerzen: monthsBetween haengt ein "T00:00:00Z"
            // an. An einen vollen Zeitstempel angehaengt ergibt das ein
            // ungueltiges Datum, monthsBetween liefert dann 0 — die Regel feuerte
            // fuer jede Position mit Zeitstempel im Protokoll nie.
            const last = log
                .filter((t) => t.serviceId === s.id && t.date && !isAccountingEntry(t))
                .map((t) => asDay(t.date))
                .sort()
                .pop();
            if (last && monthsBetween(last, todayIso) >= (config.stalePositionMonths || 6)) {
                add(
                    'stale_open_position',
                    'yellow',
                    `Position liegt seit Langem offen (${s.lvPosition || s.title})`,
                    `Letzte Bewegung am ${last}, seither ${monthsBetween(last, todayIso)} Monate ohne Abrechnung.`,
                    where,
                );
            }
        }
    }

    // ── 13. Ueberschneidender Leistungszeitraum ───────────────────────────
    // Bei kumulativer Abrechnung entfaellt die Regel: dort reicht der Zeitraum
    // JEDES Belegs vom Projektbeginn bis zum Stichtag, die Ueberschneidung ist
    // also der Normalfall und keine Auffaelligkeit. Sie trotzdem zu melden
    // hiesse, bei jeder einzelnen Rechnung zu warnen — und eine Warnung, die
    // immer erscheint, wird nicht mehr gelesen.
    for (const p of input.cumulative ? [] : input.issuedPeriods || []) {
        if (periodsOverlap(input.periodFrom, input.periodTo, p.from, p.to)) {
            add(
                'overlapping_invoice_period',
                'yellow',
                'Leistungszeitraum überschneidet eine bestehende Rechnung',
                `${p.invoiceNumber ? `Rechnung ${p.invoiceNumber}` : 'Eine bestehende Rechnung'} deckt bereits ${p.from ?? '…'} bis ${p.to ?? '…'} ab.`,
            );
            break;
        }
    }

    // ── 15. Abschlaege ueber der Gesamtleistung ───────────────────────────
    if (input.advancesCents && input.advancesCents > 0) {
        const totalNet = services.reduce(
            (sum, s) => sum + roundCents((s.quantityUsed || 0) * (s.unitPrice || 0) * 100),
            0,
        );
        if (input.advancesCents > totalNet) {
            add(
                'advance_exceeds_total',
                'red',
                'Abschläge übersteigen die erbrachte Leistung',
                `Abschläge: ${formatCents(input.advancesCents)}, erbrachte Leistung: ${formatCents(totalNet)}. Der Überhang ist ein Kundenguthaben, keine Forderung.`,
                { expected: totalNet, actual: input.advancesCents },
            );
        }
    }

    // ── 16. Nichts abzurechnen ────────────────────────────────────────────
    if (!anyOpen && !CORRECTION_TYPES.has(input.documentType) && !input.advancesCents) {
        add(
            'no_open_positions',
            'red',
            'Keine offene Position',
            'Alle erbrachten Mengen sind bereits abgerechnet oder von einem anderen Entwurf beansprucht.' +
                // Ohne den Zusatz klaenge „alles abgerechnet" wie erledigt —
                // dabei wartet gebuchte Leistung auf ihre Bestandteile.
                (anyHeld ? ' Zurückgehalten sind Paketpositionen, deren Bestandteile noch nicht vollständig sind.' : ''),
        );
    }

    // ── 17. Offene Sicherheitseinbehalte ──────────────────────────────────
    // Eine HINWEISZEILE, kein Befund: dass Geld einbehalten ist, ist der
    // vereinbarte Zustand und kein Fehler. Sie steht trotzdem hier und nicht
    // nur in der Rechnungsmaske, weil der Projektcheck die Stelle ist, an der
    // vor der Rechnung ueber das Projekt gelesen wird — und weil die naechste
    // Regel ohne diese Zahl nicht zu verstehen waere.
    const openRetentionCents = input.openRetentionCents ?? 0;
    const releasedByDraftCents = input.retentionReleaseCents ?? 0;

    if (openRetentionCents > 0) {
        add(
            'open_retention',
            'green',
            'Offene Sicherheitseinbehalte',
            `Offene Sicherheitseinbehalte: ${formatCents(openRetentionCents)}. Sie werden mit der Schlussrechnung freigegeben.`,
            { actual: openRetentionCents },
        );

        // ── 18. Schlussrechnung ohne Freigabe ─────────────────────────────
        // Gelb, nie Rot: es gibt legitime Gruende, den Einbehalt stehen zu
        // lassen (Gewaehrleistungseinbehalt nach Abnahme, strittige Leistung)
        // — im kumulativen Modell heisst das releaseRetention false, der Satz
        // bleibt und die Formel gibt nichts frei. Ohne die Warnung bliebe das
        // Geld aber unbemerkt liegen — die Schlussrechnung ist die letzte
        // Gelegenheit, es zu fordern. Die Eingabe traegt nur den Betrag, nicht
        // den Schalter: ob die Freigabe ausgeschaltet ist oder die Vorbelege
        // nicht abgezogen sind, kann diese Zeile nicht unterscheiden und nennt
        // deshalb beides.
        if (input.documentType === 'FINAL_INVOICE' && releasedByDraftCents <= 0) {
            add(
                'retention_not_released',
                'yellow',
                'Schlussrechnung gibt die Sicherheitseinbehalte nicht frei',
                `Aus früheren Belegen sind ${formatCents(openRetentionCents)} einbehalten, der Entwurf weist keine Freigabe aus — die Einbehalte bleiben stehen (Freigabe ausgeschaltet oder Vorbelege nicht abgezogen). Ohne sie bleibt der Betrag beim Auftraggeber liegen.`,
                { expected: openRetentionCents, actual: releasedByDraftCents },
            );
        }
    }

    // ── Ampel ─────────────────────────────────────────────────────────────
    const hasRed = findings.some((f) => f.traffic === 'red');
    const hasYellow = findings.some((f) => f.traffic === 'yellow');

    // blockOnRed=false ist der Notausstieg fuer Altdatenbestaende: die Befunde
    // bleiben sichtbar, sperren aber nicht mehr. Bewusst eine Einstellung und
    // kein Knopf im Dialog — wer sie setzt, entscheidet das fuer alle Projekte
    // und nicht nebenbei fuer eine einzelne Rechnung.
    const effectiveRed = hasRed && config.blockOnRed !== false;

    const traffic: ProjectCheckTraffic = effectiveRed
        ? 'red'
        : hasRed || hasYellow
          ? 'yellow'
          : 'green';

    return {
        traffic,
        findings,
        // Der Entwurf darf immer entstehen — Sichtbarkeit vor Blockade. Wer die
        // Rechnung nicht anlegen kann, sieht die Befunde nie.
        canCreateDraft: true,
        canIssue: !effectiveRed,
        requiresAcknowledgement: traffic === 'yellow',
        // Durchreichen, damit die Oberflaeche das Schweigen der Regeln 1 und 2
        // erklaeren kann, statt es wie ein bestandenes Ergebnis aussehen zu
        // lassen.
        reportExempt: input.reportExempt === true,
        // Aus demselben Grund: bei reiner Stundenabrechnung schweigt Regel 1
        // ganz und Regel 2 zur Haelfte. Ohne dieses Kennzeichen behauptet die
        // Maske "Leistungsstand, Berichte und Abrechnung passen zusammen" —
        // eine Aussage ueber eine Pruefung, die gar nicht gelaufen ist.
        hoursOnly,
    };
};

/** Kurzfassung fuer Listen und die Pruefqueue. */
export const summarizeCheck = (result: ProjectCheckResult): string => {
    if (result.traffic === 'green') return 'Keine Auffälligkeiten';
    const red = result.findings.filter((f) => f.traffic === 'red').length;
    const yellow = result.findings.filter((f) => f.traffic === 'yellow').length;
    const parts: string[] = [];
    if (red) parts.push(`${red}× rot`);
    if (yellow) parts.push(`${yellow}× gelb`);
    return parts.join(', ');
};
